import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  UserPen,
  Code,
  FilePlus,
  HelpCircle,
  LogOut,
  Bell,
  Settings,
  Landmark,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

const sidebarNavItems = [
  { to: '/dashboard/profile', label: 'Profile Editor', icon: UserPen },
  { to: '/dashboard/projects', label: 'Projects', icon: Code },
  { to: '/dashboard/create_project', label: 'Create New', icon: FilePlus },
];

export function DashboardLayout() {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-dark-surface text-dark-on-surface">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-white/5 bg-dark-background">
        {/* Brand */}
        <div className="px-6 pb-4 pt-8">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-dark-primary-container to-dark-secondary-container">
              <Landmark className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="font-headline text-lg font-black leading-none text-white">
                Precision Engine
              </h2>
              <p className="mt-1 text-[10px] uppercase tracking-widest text-dark-outline-variant">
                v2.4.0
              </p>
            </div>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="mt-4 flex-1 space-y-1 px-4">
          {sidebarNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-4 py-2 text-sm transition-all duration-200',
                  isActive
                    ? 'bg-dark-primary-container/10 font-semibold text-dark-secondary-container'
                    : 'text-dark-on-surface/60 hover:bg-dark-surface-container hover:text-white',
                )
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Deployment Status */}
        <div className="px-6 pb-4">
          <div className="rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-dark-secondary">
              Deployment Status
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="text-xs text-dark-on-surface">Systems Nominal</span>
            </div>
          </div>
        </div>

        {/* Bottom Links */}
        <div className="mt-auto space-y-1 border-t border-white/5 p-4">
          <a
            href="#"
            className="flex items-center gap-3 px-4 py-2 text-sm text-dark-on-surface/60 transition-all hover:bg-dark-surface-container hover:text-white"
          >
            <HelpCircle className="h-5 w-5" />
            Documentation
          </a>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 px-4 py-2 text-sm text-dark-on-surface/60 transition-all hover:bg-dark-surface-container hover:text-white"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="ml-64 flex min-h-screen flex-1 flex-col">
        {/* Top Header */}
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between bg-dark-background px-8">
          <div className="flex items-center gap-8">
            <h1 className="font-headline text-xl font-bold tracking-tighter text-dark-on-surface">
              Architect Admin
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative p-2 text-dark-on-surface/70 transition-opacity hover:opacity-100">
              <Bell className="h-5 w-5" />
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full border-2 border-dark-background bg-dark-secondary" />
            </button>
            <button className="p-2 text-dark-on-surface/70 transition-opacity hover:opacity-100">
              <Settings className="h-5 w-5" />
            </button>
            <div className="h-8 w-8 overflow-hidden rounded-full bg-dark-surface-container-highest ring-2 ring-dark-primary-container/20">
              <div className="flex h-full w-full items-center justify-center text-xs font-bold text-dark-on-surface">
                A
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="flex items-center justify-between border-t border-dark-outline-variant/10 bg-dark-background px-8 py-4 text-[10px] uppercase tracking-widest">
          <p className="text-dark-outline-variant">
            &copy; 2024 Precision Engineer Admin. Technical Mastery Protocol.
          </p>
          <div className="flex gap-8">
            <a
              href="#"
              className="text-dark-outline-variant transition-colors hover:text-dark-on-surface"
            >
              Support
            </a>
            <a
              href="#"
              className="text-dark-outline-variant transition-colors hover:text-dark-on-surface"
            >
              API Docs
            </a>
            <a
              href="#"
              className="text-dark-outline-variant transition-colors hover:text-dark-on-surface"
            >
              Privacy
            </a>
          </div>
        </footer>
      </div>

      {/* Visual Accents */}
      <div className="pointer-events-none fixed bottom-0 left-[256px] top-0 w-[2px] bg-dark-primary-container opacity-20" />
      <div className="pointer-events-none fixed left-[256px] right-0 top-[64px] h-[1px] bg-dark-primary-container opacity-10" />
    </div>
  );
}
